import isNil from 'lodash/isNil';
import {
  RTMMonitoringPriority,
} from 'dtos/rTMMonitoringPriority';

export const TREND_CHART_COLORS = {
  pain: '#C45C5C',
  painItemAverage: '#E8A87C',
  painItemEnjoyment: '#D98989',
  painItemActivity: '#8B3A3A',
  condition: '#3B6B9B',
  adherence: '#A67C52',
} as const;

export const PRIORITY_ACCENT_CLASSES: Record<RTMMonitoringPriority, string> = {
  [RTMMonitoringPriority.Stable]: 'border-l-green-600 bg-green-600/5',
  [RTMMonitoringPriority.Watch]: 'border-l-amber-500 bg-amber-500/5',
  [RTMMonitoringPriority.NeedsReview]: 'border-l-red-500 bg-red-500/5',
};

export const PRIORITY_VALUE_CLASSES: Record<RTMMonitoringPriority, string> = {
  [RTMMonitoringPriority.Stable]: 'text-green-800',
  [RTMMonitoringPriority.Watch]: 'text-amber-800',
  [RTMMonitoringPriority.NeedsReview]: 'text-red-800',
};

export const PRIORITY_BADGE_CLASSES: Record<RTMMonitoringPriority, string> = {
  [RTMMonitoringPriority.Stable]: 'bg-green-100 text-green-800',
  [RTMMonitoringPriority.Watch]: 'bg-amber-100 text-amber-800',
  [RTMMonitoringPriority.NeedsReview]: 'bg-red-100 text-red-800',
};

const FALLBACK_PRIORITY_ACCENT_CLASS = 'border-l-gray-400 bg-gray-400/5';
const FALLBACK_PRIORITY_VALUE_CLASS = 'text-gray-900';

export function getPriorityAccentClass(priority?: RTMMonitoringPriority): string {
  return isNil(priority)
    ? FALLBACK_PRIORITY_ACCENT_CLASS
    : PRIORITY_ACCENT_CLASSES[priority];
}

export function getPriorityValueClass(priority?: RTMMonitoringPriority): string {
  return isNil(priority)
    ? FALLBACK_PRIORITY_VALUE_CLASS
    : PRIORITY_VALUE_CLASSES[priority];
}

export type MetricTone = 'default' | 'info' | 'warning' | 'danger';

export type MetricTheme = 'neutral' | 'pain' | 'condition' | 'adherence';

export type SeriesTrendDirection = 'lowerBetter' | 'higherBetter';

export type SeriesTrendLabel = 'No change' | 'Improving' | 'Worsening';

export const METRIC_TONE_BADGE_CLASSES: Record<
MetricTheme,
Record<MetricTone, string>
> = {
  neutral: {
    default: 'bg-gray-100 text-gray-700',
    info: 'bg-blue-100 text-blue-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
  },
  pain: {
    default: 'bg-gray-100 text-gray-700',
    info: 'bg-red-50 text-red-700',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
  },
  condition: {
    default: 'bg-gray-100 text-gray-700',
    info: 'bg-blue-100 text-blue-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
  },
  adherence: {
    default: 'bg-gray-100 text-gray-700',
    info: 'bg-orange-100 text-orange-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
  },
};

export const METRIC_TONE_VALUE_CLASSES: Record<
MetricTheme,
Record<MetricTone, string>
> = {
  neutral: {
    default: 'text-gray-900',
    info: 'text-blue-800',
    warning: 'text-amber-800',
    danger: 'text-red-800',
  },
  pain: {
    default: 'text-gray-900',
    info: 'text-red-700',
    warning: 'text-amber-800',
    danger: 'text-red-800',
  },
  condition: {
    default: 'text-gray-900',
    info: 'text-blue-800',
    warning: 'text-amber-800',
    danger: 'text-red-800',
  },
  adherence: {
    default: 'text-gray-900',
    info: 'text-orange-800',
    warning: 'text-amber-800',
    danger: 'text-red-800',
  },
};

export const CONDITION_RANK_LABELS: Record<number, string> = {
  7: 'Much better',
  6: 'Moderately better',
  5: 'A little better',
  4: 'About the same',
  3: 'Slightly worse',
  2: 'Moderately worse',
  1: 'Much worse',
};

export const ADHERENCE_RANK_LABELS: Record<number, string> = {
  5: 'Always or almost always',
  4: 'Most of the time',
  3: 'Sometimes',
  2: 'Rarely',
  1: 'Never',
};

export const PAIN_Y_DOMAIN: [number, number] = [0, 10];
export const PAIN_Y_TICKS = [0, 2, 4, 6, 8, 10];

export const CONDITION_Y_DOMAIN: [number, number] = [1, 7];
export const CONDITION_Y_TICKS = [1, 2, 3, 4, 5, 6, 7];
export const CONDITION_REFERENCE_Y = 4;

export const ADHERENCE_Y_DOMAIN: [number, number] = [1, 5];
export const ADHERENCE_Y_TICKS = [1, 2, 3, 4, 5];
export const ADHERENCE_REFERENCE_Y = 4;

export const PAIN_ITEM_SERIES = [
  {
    yKey: 'averagePain',
    color: TREND_CHART_COLORS.painItemAverage,
    name: 'Average pain',
  },
  {
    yKey: 'painInterferenceEnjoyment',
    color: TREND_CHART_COLORS.painItemEnjoyment,
    name: 'Enjoyment',
  },
  {
    yKey: 'painInterferenceActivity',
    color: TREND_CHART_COLORS.painItemActivity,
    name: 'Activity',
  },
] as const;
